import { useEffect, useRef, useState } from 'react';
import { locationService } from '@iso/services';
import { isSuccess } from '@iso/lib/helpers/apiResponse';

/**
 * Reports the signed-in user's own position from the browser, so the "Share my
 * location" switch does something for somebody on a computer.
 *
 * This is the WEB half of the contract and the only half the sharing toggle
 * governs. A mobile report carries its own consent — a contractor shares by
 * checking in with SafeFarm on — and is never gated by this switch.
 *
 * Deliberately narrow, because this is somebody's whereabouts:
 *
 *  - It runs only while `enabled` is true, which means the layer is open AND
 *    the user has sharing switched on. Closing the map stops it. Nothing
 *    reports in the background.
 *  - It asks the browser for a position only when it is about to send one.
 *    A denied or unavailable permission is remembered and not re-asked on a
 *    timer, so the user is prompted once rather than every interval.
 *  - The server re-checks the preference on every report, so a stale tab that
 *    somehow kept running cannot keep a position warm after the user opts out.
 *
 * @param {number|string} propertyId
 * @param {boolean} enabled
 * @param {number} intervalMs
 * @param {function} onReported Called after a position is accepted, so the
 *                              caller can refresh the list without waiting for
 *                              its own poll.
 * @returns {{ status: string, detail: string|null }} status is
 *          'idle' | 'reporting' | 'refused' | 'denied' | 'unavailable'.
 *          detail is the server's reason for a refusal, null otherwise.
 */
export const useBrowserLocationReporter = (propertyId, enabled, intervalMs, onReported) => {
  const [status, setStatus] = useState('idle');
  // The server's reason for a refusal. Kept beside the status because the
  // causes are not distinguishable from here and read very differently to the
  // person: an imprecise fix is theirs to act on, sharing switched off in
  // another tab is not.
  const [detail, setDetail] = useState(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  // A refused or broken permission is permanent for this mount: re-prompting
  // every interval would be the browser equivalent of nagging.
  const blocked = useRef(false);
  const onReportedRef = useRef(onReported);
  onReportedRef.current = onReported;

  useEffect(() => () => { mounted.current = false; }, []);

  // A fresh property, or switching sharing back on, deserves another try.
  useEffect(() => {
    blocked.current = false;
    if (mounted.current) {
      setStatus('idle');
    }
  }, [propertyId, enabled]);

  useEffect(() => {
    if (!enabled || !propertyId) {
      return undefined;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('unavailable');
      return undefined;
    }

    const send = () => {
      if (blocked.current || inFlight.current || !mounted.current) {
        return;
      }
      // Nothing useful comes of reporting a position nobody is looking at,
      // and a hidden tab's fix is usually the stalest one anyway.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }

      inFlight.current = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!mounted.current) {
            inFlight.current = false;
            return;
          }
          locationService
            .reportMine(propertyId, {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracyMeters: pos.coords.accuracy,
              // The device's own capture time, which is what relative time
              // counts from — not the moment the request happened to be sent.
              recordedAt: new Date(pos.timestamp).toISOString(),
            })
            .then((res) => {
              if (!mounted.current) {
                return;
              }
              // A 200 is not an accepted report. The server refuses with
              // success:false when the fix is too imprecise, when sharing was
              // switched off in another tab, and when the caller is not a
              // member. Treating those as success would claim "reporting"
              // while nothing is being stored.
              //
              // A position the server deliberately ignores in favour of a
              // better one -- a current app fix, or a newer stored fix -- is
              // NOT one of these. Those return success:true, because the
              // report was received and answered correctly: nothing is wrong,
              // and the panel should say nothing.
              if (!isSuccess(res)) {
                setDetail((res && res.message) || null);
                setStatus('refused');
                return;
              }
              setDetail(null);
              setStatus('reporting');
              if (typeof onReportedRef.current === 'function') {
                onReportedRef.current();
              }
            })
            .catch(() => {
              // A dropped report is not worth interrupting anyone over; the
              // next tick tries again.
            })
            .finally(() => {
              inFlight.current = false;
            });
        },
        (err) => {
          inFlight.current = false;
          if (!mounted.current) {
            return;
          }
          // 1 = PERMISSION_DENIED. Anything else (position unavailable,
          // timeout) is reported as unavailable, but both stop the loop:
          // a device that cannot produce a fix will not start mid-session.
          blocked.current = true;
          setDetail(null);
          setStatus(err && err.code === 1 ? 'denied' : 'unavailable');
        },
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 30000 }
      );
    };

    send();
    const timer = setInterval(send, intervalMs);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        send();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [propertyId, enabled, intervalMs]);

  return { status, detail };
};

export default useBrowserLocationReporter;
